import React, { useState } from "react";
import * as XLSX from "xlsx";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Upload, Download, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";

const REQUIRED_COLS = ["item_code", "item_name"];
const OPTIONAL_COLS = ["brand", "category", "hsn_code", "gst_rate", "unit", "purchase_price", "sale_price", "current_stock", "reorder_level"];
const ALL_COLS = [...REQUIRED_COLS, ...OPTIONAL_COLS];

export default function ImportItemsDialog({ open, onOpenChange, onImported }) {
  const [rows, setRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const reset = () => { setRows([]); setFileName(""); setResult(null); };

  const downloadTemplate = () => {
    const template = [{
      item_code: "HW-001", item_name: "Water Purifier Filter", brand: "Hindware",
      category: "Filter", hsn_code: "84212100", gst_rate: 18, unit: "PCS",
      purchase_price: 450, purchase_inclusive: false,
      sale_price: 944, sale_inclusive: true,
      current_stock: 10, reorder_level: 5,
    }, {
      item_code: "LP-101", item_name: "RO Membrane 80 GPD", brand: "Livpure",
      category: "Membrane", hsn_code: "84212100", gst_rate: 18, unit: "PCS",
      purchase_price: 1200, purchase_inclusive: false,
      sale_price: 2360, sale_inclusive: true,
      current_stock: 5, reorder_level: 2,
    }];
    const cols = ["item_code","item_name","brand","category","hsn_code","gst_rate","unit","purchase_price","purchase_inclusive","sale_price","sale_inclusive","current_stock","reorder_level"];
    const ws = XLSX.utils.json_to_sheet(template, { header: cols });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Items");
    XLSX.writeFile(wb, "items_template.xlsx");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "binary" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: "" });
        // normalize rows
        const norm = data.map(r => {
          const out = {};
          Object.keys(r).forEach(k => {
            const key = String(k).trim().toLowerCase().replace(/\s+/g, "_");
            out[key] = r[k];
          });
          return {
            item_code: String(out.item_code || "").trim(),
            item_name: String(out.item_name || "").trim(),
            brand: String(out.brand || "").trim(),
            category: String(out.category || "").trim(),
            hsn_code: String(out.hsn_code || "").trim(),
            gst_rate: parseFloat(out.gst_rate) || 0,
            unit: String(out.unit || "PCS").trim() || "PCS",
            purchase_price: parseFloat(out.purchase_price) || 0,
            purchase_inclusive: String(out.purchase_inclusive).toLowerCase() === "true" || out.purchase_inclusive === true,
            sale_price: parseFloat(out.sale_price) || 0,
            sale_inclusive: String(out.sale_inclusive).toLowerCase() === "true" || out.sale_inclusive === true,
            current_stock: parseFloat(out.current_stock) || 0,
            reorder_level: parseFloat(out.reorder_level) || 0,
          };
        }).filter(r => r.item_code && r.item_name);
        if (!norm.length) {
          toast.error("No valid rows found. Ensure item_code and item_name columns are present.");
          return;
        }
        setRows(norm);
        toast.success(`${norm.length} rows loaded — preview below`);
      } catch (err) {
        toast.error("Failed to parse file. Use .xlsx or .csv");
      }
    };
    reader.readAsBinaryString(f);
  };

  const submit = async () => {
    if (!rows.length) return toast.error("No rows to import");
    setBusy(true);
    try {
      const { data } = await api.post("/items/bulk-import", rows);
      setResult(data);
      toast.success(`Import complete: ${data.created} created, ${data.updated} updated`);
      onImported?.();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display">Import Items from Excel / CSV</DialogTitle>
        </DialogHeader>

        <Card className="p-4 bg-secondary/40 border border-border">
          <div className="text-sm space-y-1">
            <p><b>Step 1:</b> Download the template Excel file (with correct columns)</p>
            <p><b>Step 2:</b> Fill in your item data. <b>Required columns</b>: item_code, item_name.</p>
            <p><b>Step 3:</b> Upload the filled file. If item_code exists, it will be updated; else new item created.</p>
          </div>
          <div className="mt-3 flex gap-2">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="import-download-template">
              <Download size={14} className="mr-1" /> Download Template
            </Button>
            <label className="inline-flex items-center gap-2 px-3 py-1.5 border border-border rounded cursor-pointer hover:bg-secondary text-sm" data-testid="import-file-label">
              <Upload size={14} />
              <span>{fileName || "Choose .xlsx / .csv file"}</span>
              <input type="file" accept=".xlsx,.xls,.csv" onChange={onFile} className="hidden" data-testid="import-file-input" />
            </label>
          </div>
        </Card>

        {rows.length > 0 && !result && (
          <div>
            <p className="text-sm font-semibold mb-2">Preview ({rows.length} rows):</p>
            <div className="border border-border rounded max-h-64 overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Code</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Brand</TableHead>
                    <TableHead className="text-right">GST%</TableHead>
                    <TableHead className="text-right">Purchase</TableHead>
                    <TableHead className="text-right">Sale</TableHead>
                    <TableHead className="text-right">Stock</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.slice(0, 50).map((r, i) => (
                    <TableRow key={i}>
                      <TableCell className="font-mono text-xs">{r.item_code}</TableCell>
                      <TableCell>{r.item_name}</TableCell>
                      <TableCell>{r.brand}</TableCell>
                      <TableCell className="text-right">{r.gst_rate}</TableCell>
                      <TableCell className="text-right">{r.purchase_price}</TableCell>
                      <TableCell className="text-right">{r.sale_price}</TableCell>
                      <TableCell className="text-right">{r.current_stock}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {rows.length > 50 && <p className="text-xs text-muted-foreground mt-2">Showing first 50 rows. Total: {rows.length}</p>}
          </div>
        )}

        {result && (
          <Card className="p-4 border border-green-200 bg-green-50">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle size={20} className="text-green-600" weight="fill" />
              <span className="font-semibold">Import Complete</span>
            </div>
            <div className="text-sm space-y-1">
              <p>✅ <b>Created</b>: {result.created} new items</p>
              <p>🔄 <b>Updated</b>: {result.updated} existing items</p>
              <p>📊 <b>Total processed</b>: {result.total}</p>
              {result.errors?.length > 0 && (
                <div className="mt-2">
                  <p className="text-destructive font-semibold">Errors ({result.errors.length}):</p>
                  <ul className="text-xs list-disc pl-5">
                    {result.errors.slice(0, 10).map((e, i) => <li key={i}>Row {e.row}: {e.error}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </Card>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }} data-testid="import-close">
            {result ? "Close" : "Cancel"}
          </Button>
          {!result && (
            <Button onClick={submit} disabled={busy || !rows.length} data-testid="import-submit" className="bg-[hsl(24.6_95%_53.1%)] hover:bg-[hsl(24.6_95%_47%)] text-white">
              {busy ? "Importing..." : `Import ${rows.length} Items`}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
